import { forwardRef } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Button compartilhado — variants e sizes consistentes em toda a plataforma.
 *
 * Substitui os 60+ botões inline com `inline-flex items-center gap-1.5 px-3
 * py-1.5 rounded-md bg-X-500 text-white text-xs ...` espalhados pelo código.
 *
 * Variants:
 *   - primary: brand (CTA principal)
 *   - secondary: surface com border (ações secundárias)
 *   - ghost: texto + hover sutil (ações terciárias / "voltar")
 *   - danger: vermelho (delete, opt-out)
 *   - success: emerald (confirmar, ativar)
 *
 * Sizes (touch-targets ≥36px sm, ≥44px md):
 *   - sm: 28px (buttons em tabela, chips de ação)
 *   - md: 36px (default)
 *   - lg: 44px (CTAs principais, mobile-friendly)
 */

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "success";
type ButtonSize = "sm" | "md" | "lg";

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
};

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    "bg-brand-500 text-white border border-brand-500 hover:bg-brand-600 hover:border-brand-600 active:bg-brand-700 disabled:bg-brand-300 disabled:border-brand-300 dark:disabled:bg-brand-500/40 dark:disabled:border-brand-500/40",
  secondary:
    "bg-surface text-fg border border-border-base hover:bg-surface-2 hover:border-border-strong active:bg-bg disabled:opacity-50",
  ghost:
    "bg-transparent text-fg-muted border border-transparent hover:bg-surface-2 hover:text-fg active:bg-bg disabled:opacity-50",
  danger:
    "bg-red-600 text-white border border-red-600 hover:bg-red-700 hover:border-red-700 active:bg-red-800 disabled:bg-red-300 disabled:border-red-300",
  success:
    "bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 active:bg-emerald-800 disabled:bg-emerald-300 disabled:border-emerald-300",
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: "h-7 px-2.5 text-[11px] gap-1 rounded-md",
  md: "h-9 px-3.5 text-xs gap-1.5 rounded-md",
  lg: "h-11 px-5 text-sm gap-2 rounded-lg",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = "primary",
      size = "md",
      loading,
      icon,
      iconRight,
      disabled,
      className,
      children,
      ...rest
    },
    ref
  ) {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          "inline-flex items-center justify-center font-medium",
          "transition-colors duration-150",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
          "disabled:cursor-not-allowed",
          VARIANT_CLASSES[variant],
          SIZE_CLASSES[size],
          className
        )}
        {...rest}
      >
        {loading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : icon ? (
          <span className="shrink-0">{icon}</span>
        ) : null}
        {children && <span className="truncate">{children}</span>}
        {iconRight && !loading && <span className="shrink-0">{iconRight}</span>}
      </button>
    );
  }
);
